import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface GitHubAsset {
  download_count: number;
}

interface GitHubRelease {
  assets: GitHubAsset[];
}

const Footer: React.FC = () => {
  const { language } = useLanguage();
  const [repoStats, setRepoStats] = useState({
    stars: 32,
    forks: 6,
    license: 'GPL-3.0',
    downloads: 0
  });
  const [loading, setLoading] = useState(true);

  const content = {
    en: {
      stats: {
        stars: 'Stars',
        forks: 'Forks',
        downloads: 'Downloads'
      }
    },
    ja: {
      stats: {
        stars: 'スター',
        forks: 'フォーク',
        downloads: 'ダウンロード'
      }
    }
  };

  const t = content[language];

  useEffect(() => {
    const fetchRepoData = async () => {
      try {
        const repoResponse = await fetch('https://api.github.com/repos/TheProjectLumina/LuminaClient');
        let stars = 32;
        let forks = 6;
        let license = 'GPL-3.0';

        if (repoResponse.ok) {
          const repoData = await repoResponse.json();
          stars = repoData.stargazers_count;
          forks = repoData.forks_count;
          license = repoData.license?.spdx_id || 'GPL-3.0';
        }

        const releasesResponse = await fetch('https://api.github.com/repos/TheProjectLumina/LuminaClient/releases');
        let totalDownloads = 0;

        if (releasesResponse.ok) {
          const releasesData = await releasesResponse.json() as GitHubRelease[];

          totalDownloads = releasesData.reduce((total: number, release: GitHubRelease) => {
            const releaseDownloads = release.assets.reduce((sum: number, asset: GitHubAsset) => sum + asset.download_count, 0);
            return total + releaseDownloads;
          }, 0);
        }

        setRepoStats({
          stars,
          forks,
          license,
          downloads: totalDownloads
        });
      } catch (error) {
        console.error('Error fetching repo data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRepoData();
  }, []);

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-content">
          <div className="footer-logo">
            <div className="moon-logo"></div>
            <span>Project Lumina</span>
          </div>
          <div className="footer-stats-section">
            <h3 className="footer-stats-title">Repository Stats</h3>
            <div className="footer-stats">
              <div className="footer-stat-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" />
                </svg>
                <span>{loading ? '...' : `${repoStats.stars} ${t.stats.stars}`}</span>
              </div>
              <div className="footer-stat-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 3C6 3 7 3 7 4C7 5 6 5 6 5H3C3 5 2 5 2 6V21C2 21 2 22 3 22H17C17 22 18 22 18 21C18 20 17 20 17 20H5V7H6C6 7 7 7 7 8C7 9 6 9 6 9H8C8 9 9 9 9 8C9 7 8 7 8 7C8 7 8 5 6 3Z" fill="currentColor" />
                  <path d="M13 5C13 5 12 5 12 6V10H10C10 10 9 10 9 11C9 12 10 12 10 12H12V14H10C10 14 9 14 9 15C9 16 10 16 10 16H12V18C12 18 12 19 13 19C14 19 14 18 14 18V16H16C16 16 17 16 17 15C17 14 16 14 16 14H14V12H16C16 12 17 12 17 11C17 10 16 10 16 10H14V6C14 6 14 5 13 5Z" fill="currentColor" />
                </svg>
                <span>{loading ? '...' : `${repoStats.forks} ${t.stats.forks}`}</span>
              </div>
              <div className="footer-stat-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 3V16M12 16L7 11M12 16L17 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 20H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span>{loading ? '...' : `${repoStats.downloads.toLocaleString()} ${t.stats.downloads}`}</span>
              </div>
              <div className="footer-stat-item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.5 2H6C4.89 2 4 2.89 4 4V20C4 21.11 4.89 22 6 22H18C19.11 22 20 21.11 20 20V7.5L14.5 2ZM16 14V16H8V14H16ZM16 11V13H8V11H16ZM13 8V3.5L17.5 8H13Z" fill="currentColor" />
                </svg>
                <span>{repoStats.license}</span>
              </div>
            </div>
          </div>
          <div className="footer-links-section">
            <h3 className="footer-links-title">Quick Links</h3>
            <div className="footer-links">
              <a href="https://github.com/TheProjectLumina/LuminaClient" className="footer-link">
                <span>GitHub</span>
              </a>
              <Link to="/discord" className="footer-link">
                <span>Discord</span>
              </Link>
              <a href="https://deepwiki.com/TheProjectLumina/LuminaClient" className="footer-link" target="_blank" rel="noopener noreferrer">
                <span>Documentation</span>
              </a>
              <Link to="/privacy-policy" className="footer-link">
                <span>Privacy Policy</span>
              </Link>
            </div>
          </div>
        </div>
        <div className="copyright">
          &copy; {new Date().getFullYear()} Project Lumina. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
